import React from "react";
import { VakQuestion } from "../../typings/vak";
import VakAnswerSummary from "./VakAnswerSummary";
import { Progress } from "../UI/Progress";
import { getAnswerOptions } from "../../utils/vak";

interface VakTestQuestionProps {
  questions: VakQuestion[];
  currentQuestionIndex: number;
  answers: { [key: number]: number };
  loading: boolean;
  showAnswersSummary: boolean;
  onAnswer: (questionId: number, answer: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
  onToggleSummary: (show: boolean) => void;
}

const RiasecTestQuestion: React.FC<VakTestQuestionProps> = ({
  questions,
  currentQuestionIndex,
  answers,
  loading,
  showAnswersSummary,
  onAnswer,
  onNext,
  onPrev,
  onSubmit,
  onToggleSummary,
}) => {
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const isCurrentAnswered = currentQuestion
    ? answers.hasOwnProperty(currentQuestion.id)
    : false;
  const allAnswered = answeredCount === totalQuestions;

  return (
    <div className="riasec-container">
      {/* Top Progress Section */}
      <div className="test-header">
        <div className="w-full">
          <div className="flex flex-wrap">
            <div className="w-full px-3">
              <div className="progress-section">
                <div className="flex justify-between items-center mb-2">
                  <span className="question-counter">
                    {currentQuestionIndex} / {totalQuestions}
                  </span>
                  <span className="progress-text">
                    {Math.round((currentQuestionIndex / totalQuestions) * 100)}%
                  </span>
                </div>
                <Progress
                  value={((currentQuestionIndex + 1) / totalQuestions) * 100}
                  className="flex-1 h-2 mt-2 bg-muted"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="test-content py-8 px-3">
        <div className="w-full p-0">
          <div className="flex flex-wrap justify-center">
            <div className="w-full md:w-8/12 lg:w-6/12">
              <div className="question-card">
                {loading && (
                  <div className="question-loading-overlay">
                    <div
                      className="animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 text-primary"
                      role="status"
                    >
                      <span className="sr-only"></span>
                    </div>
                  </div>
                )}

                <div className="question-text">{currentQuestion?.question}</div>

                <div className="answer-buttons">
                  {
                    getAnswerOptions().map(option => {
                      return (
                        <button
                          className={`answer-btn ${
                            answers[currentQuestion?.id] === option.score
                              ? "active"
                              : ""
                          }`}
                          onClick={() => onAnswer(currentQuestion.id, option.score)}
                          disabled={loading}
                        >
                          {option.label}
                        </button>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="test-navigation px-3 py-4">
        <div className="w-full">
          <div className="flex flex-wrap">
            <div className="w-full">
              <div className="nav-controls">
                <button
                  className="nav-btn nav-btn-prev"
                  onClick={onPrev}
                  disabled={currentQuestionIndex === 0}
                >
                  <div className="nav-icon">←</div>
                  <div className="nav-text">Sebelumnya</div>
                </button>

                <button
                  className="nav-btn nav-btn-summary"
                  onClick={() => onToggleSummary(true)}
                >
                  <small>Ringkasan</small>
                  <div className="summary-count">
                    {answeredCount}/{totalQuestions}
                  </div>
                </button>

                {currentQuestionIndex === totalQuestions - 1 ? (
                  <button
                    className="nav-btn nav-btn-submit"
                    onClick={onSubmit}
                    disabled={!allAnswered || loading}
                  >
                    {loading ? "Mengirim..." : "Selesai"}
                  </button>
                ) : (
                  <button
                    className="nav-btn nav-btn-next"
                    onClick={onNext}
                    disabled={!isCurrentAnswered}
                  >
                    <div className="nav-icon">→</div>
                    <div className="nav-text">Selanjutnya</div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAnswersSummary && (
        <VakAnswerSummary
          questions={questions}
          answers={answers}
          onClose={() => onToggleSummary(false)}
          show={showAnswersSummary}
        />
      )}
    </div>
  );
};

export default RiasecTestQuestion;
